import qs from 'qs';
import PropTypes from 'prop-types';
import * as React from 'react';
import AuthenticationView from 'auth/components/AuthenticationView';
import CurrentUser from 'auth/CurrentUser';
import SignInForm from 'auth/components/SignInForm';
import { withDocumentTitle } from 'lib/documentTitle';
import EventTracking from 'lib/EventTracking';
import { getUrlOnAuth } from 'lib/ClientRouteLogic';
import GTMScript from 'components/GTMScript';
import GoogleAPI from 'lib/GoogleAPI';
import { completeGoogleJoin } from 'join/completeGoogleJoin';

type Redirector = {
  onSignInRedirectTo?: any;
  attemptedTransition?: any;
  reset: () => void;
};

// TODO: Not sure why we have two of those.
// they are consumed the same way (router.push).
export const redirector: Redirector = {
  onSignInRedirectTo: null,
  attemptedTransition: null,
  reset() {
    this.onSignInRedirectTo = null;
    this.attemptedTransition = null;
  },
};

const OAUTH_QUERY_KEYS = new Set([
  'code',
  'state',
  'error',
  'error_description',
  'scope',
  'authuser',
  'prompt',
  'hd',
  'iss',
]);

type Props = {
  location: any;
};

class SignInPage extends React.Component<Props> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };
  componentDidMount() {
    if (redirector.attemptedTransition) {
      const queryStringWithNoQuestionMark = redirector.attemptedTransition.search.replace('?', '');
      const queryParams = qs.parse(queryStringWithNoQuestionMark);
      if ('queryParams' in queryParams) {
        const transitionParams = queryParams.queryParams;
        if ('isShareLink' in transitionParams) {
          EventTracking.track('share_on_remind.not_logged_in', {
            referrer: transitionParams.referrer,
            url: transitionParams.link,
          });
        }
      }
    }

    CurrentUser.addSignInListener(this.onSignIn);
    CurrentUser.addSignInNoRoleListener(this.onUserNoRoleSignin);
  }

  componentWillUnmount() {
    CurrentUser.removeSignInListener(this.onSignIn);
    CurrentUser.removeSignInNoRoleListener(this.onUserNoRoleSignin);
  }

  onSignIn = () => {
    const pending = GoogleAPI.takePendingReturn();
    const mergedQuery = {
      ...(pending && pending.extraQuery ? pending.extraQuery : {}),
      ...this.props.location.query,
      ...(pending && pending.join ? { join: pending.join } : {}),
      ...(pending && pending.locationOnAuth ? { locationOnAuth: pending.locationOnAuth } : {}),
      ...(pending && pending.urlOnAuth ? { urlOnAuth: pending.urlOnAuth } : {}),
    };
    const query = Object.keys(mergedQuery).reduce((acc, key) => {
      if (!OAUTH_QUERY_KEYS.has(key)) {
        acc[key] = mergedQuery[key];
      }
      return acc;
    }, {} as Record<string, string>);
    const { attemptedTransition, onSignInRedirectTo } = redirector;

    if (attemptedTransition) {
      redirector.reset();
      this.context.router.push(attemptedTransition);
      return;
    }

    if (onSignInRedirectTo) {
      redirector.reset();
      this.context.router.push(onSignInRedirectTo);
      return;
    }

    // urlOnAuth is for pages outside r101-frontend, but still on remind.com, and causes a refresh
    // locationOnAuth is for pages on r101-frontend, and does not cause a refresh
    const urlOnAuth = getUrlOnAuth({ ...this.props.location, query });
    if (urlOnAuth) {
      window.location.replace(urlOnAuth);
    } else if (query.locationOnAuth) {
      if (pending && pending.locationOnAuth === '/landing') {
        EventTracking.trackKey('tos_and_pp.view');
      }
      const { locationOnAuth, ...rest } = query;
      this.context.router.push({
        pathname: locationOnAuth,
        query: rest,
      });
    } else if (pending && pending.join) {
      completeGoogleJoin(pending.join, this.context.router);
    } else if (query.join) {
      this.context.router.push(`/join/${query.join}`);
    } else {
      this.context.router.push('/classes');
    }
  };
  onUserNoRoleSignin = () => {
    const pending = GoogleAPI.takePendingReturn();
    const join = (pending && pending.join) || this.props.location.query.join;
    if (join) {
      this.context.router.push(`/join/role_picker/${join}`);
      return;
    }

    // /sign_up Google used to call JoinSignUpPage.onUserCreate → /landing.
    const locationOnAuth =
      (pending && pending.locationOnAuth) || this.props.location.query.locationOnAuth;
    if (locationOnAuth) {
      if (locationOnAuth === '/landing') {
        EventTracking.trackKey('tos_and_pp.view');
      }
      this.context.router.push(locationOnAuth);
      return;
    }

    this.context.router.push('/join/role_picker');
  };
  render() {
    const { query } = this.props.location;

    return (
      <AuthenticationView {...this.props} title="Log in">
        <GTMScript />
        <SignInForm
          initialUid={query.email || query.uid || ''}
          emailConfirmationToken={query.email_token}
        />
      </AuthenticationView>
    );
  }
}

export { SignInPage };
export default withDocumentTitle('Sign in', SignInPage);
